import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive } from 'lucide-react';
import { SortableTh, sortRows, BulkActionsBar, PillGroup } from '@synotis/ui';
import { apiAffaires } from '../api/client.js';
import Modal from './Modal.jsx';
import InternalAffaireBadge from './InternalAffaireBadge.jsx';

/**
 * synotis-support#19 — Suggestions d'archivage (ADMIN).
 *
 * Liste les affaires sans pointage depuis N mois et sans planning à venir
 * (`GET /api/affaires/archive-suggestions`), avec leurs raisons. Rien n'est
 * archivé sans action explicite : l'admin coche, confirme, et chaque affaire
 * passe par la route unitaire `DELETE /api/affaires/:id` (mêmes gardes, audit
 * `AFFAIRE_ARCHIVE`, restauration possible). Un échec n'arrête pas les autres.
 */

const THRESHOLDS = [3, 6, 12];

const STATUS_LABELS = { TENTATIVE: 'Tentative', CONFIRMED: 'Confirmé', COMPLETED: 'Terminé' };

const PLANNING_ONLY_TITLE =
  'En prévision : affaire issue d’une prospection Synosight (saisie des temps fermée). ' +
  'Elle peut encore être gagnée : vérifiez avant d’archiver.';

const th = { padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 };
const td = { padding: '8px 12px', verticalAlign: 'top' };

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

function fmtDay(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function PlanningOnlyBadge() {
  return (
    <span
      title={PLANNING_ONLY_TITLE}
      style={{
        display: 'inline-block',
        padding: '0 6px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 10,
        fontWeight: 500,
        lineHeight: '16px',
        background: 'var(--glass)',
        color: 'var(--warning)',
        border: '1px solid var(--warning)',
        whiteSpace: 'nowrap',
      }}
    >
      En prévision
    </span>
  );
}

export default function ArchiveSuggestionsModal({ open, onClose, onArchived }) {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [kind, setKind] = useState('all');
  const [sort, setSort] = useState({ field: 'lastActivity', dir: 'asc' });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiAffaires.archiveSuggestions(months));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [months]);

  // Ouverture ou changement de seuil : sélection et compte rendu repartent à zéro.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setReport(null);
    load();
  }, [open, load]);

  const items = data?.items ?? [];
  const counts = useMemo(
    () => ({
      all: items.length,
      planning: items.filter((a) => a.planningOnly).length,
      internal: items.filter((a) => a.isInternal).length,
    }),
    [items],
  );
  const visible = useMemo(() => {
    const filtered =
      kind === 'planning' ? items.filter((a) => a.planningOnly)
        : kind === 'internal' ? items.filter((a) => a.isInternal)
          : items;
    return sortRows(filtered, sort, {
      name: (a) => a.name,
      code: (a) => a.code,
      client: (a) => a.client?.name,
      lastActivity: (a) => a.lastActivityDate,
      lastLogged: (a) => a.lastLoggedDate,
      lastPlanned: (a) => a.lastPlannedDate,
      status: (a) => STATUS_LABELS[a.status] ?? a.status,
    });
  }, [items, kind, sort]);

  const selected = items.filter((a) => selectedIds.has(a.id));
  const allVisibleSelected = visible.length > 0 && visible.every((a) => selectedIds.has(a.id));
  const toggle = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((a) => next.delete(a.id));
      else visible.forEach((a) => next.add(a.id));
      return next;
    });

  const onArchiveSelection = async () => {
    const n = selected.length;
    const planning = selected.filter((a) => a.planningOnly).length;
    const internal = selected.filter((a) => a.isInternal).length;
    const warnings = [
      planning
        ? `⚠ ${planning} affaire${planning > 1 ? 's' : ''} « En prévision » (prospection Synosight encore ouverte possible).`
        : null,
      internal ? `⚠ ${internal} affaire${internal > 1 ? 's' : ''} interne${internal > 1 ? 's' : ''}.` : null,
    ].filter(Boolean);
    const message =
      `Archiver ${n} affaire${n > 1 ? 's' : ''} ?\n\n` +
      (warnings.length ? `${warnings.join('\n')}\n\n` : '') +
      'Aucune donnée n’est supprimée : une affaire archivée se restaure depuis le filtre « Archivés ».';
    if (!window.confirm(message)) return;

    setBusy(true);
    setError(null);
    const failures = [];
    let archived = 0;
    for (const a of selected) {
      try {
        await apiAffaires.archive(a.id);
        archived += 1;
      } catch (err) {
        failures.push(`${a.name} : ${err.message}`);
      }
    }
    setBusy(false);
    setSelectedIds(new Set());
    setReport({ archived, failures });
    await load();
    if (archived > 0) onArchived?.();
  };

  const bulkActions = [
    {
      key: 'archive',
      label: 'Archiver la sélection',
      icon: Archive,
      color: 'var(--warning)',
      count: selected.length,
      onClick: onArchiveSelection,
    },
  ];

  const subtitle = data
    ? `Aucun pointage avant le ${fmtDay(data.cutoffDate)} et aucune allocation en cours ou à venir. Rien n’est archivé sans votre validation.`
    : 'Affaires sans pointage récent et sans planning à venir.';

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title="Suggestions d’archivage"
      subtitle={subtitle}
      size="xl"
      fill
      footer={
        <button type="button" onClick={onClose} disabled={busy} style={secondaryBtn}>
          Annuler
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, flex: 1 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sans pointage depuis</span>
            <PillGroup
              ariaLabel="Seuil d’inactivité"
              options={THRESHOLDS.map((m) => ({ value: m, label: `${m} mois` }))}
              value={months}
              onChange={setMonths}
            />
          </div>
          <PillGroup
            ariaLabel="Type d’affaire"
            options={[
              { value: 'all', label: `Toutes (${counts.all})` },
              { value: 'planning', label: `En prévision (${counts.planning})` },
              { value: 'internal', label: `Internes (${counts.internal})` },
            ]}
            value={kind}
            onChange={setKind}
          />
        </div>

        {error ? <div style={{ color: 'var(--danger)' }}>{error}</div> : null}
        {report ? (
          <div
            role="status"
            style={{ fontSize: 13, color: report.failures.length ? 'var(--danger)' : 'var(--success)' }}
          >
            {report.archived} affaire{report.archived > 1 ? 's' : ''} archivée{report.archived > 1 ? 's' : ''}
            {report.failures.length
              ? ` — ${report.failures.length} échec(s) : ${report.failures.join(' · ')}`
              : '.'}
          </div>
        ) : null}

        <BulkActionsBar
          count={selected.length}
          actions={bulkActions}
          busy={busy}
          onClear={() => setSelectedIds(new Set())}
        />

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
                <th style={{ ...th, width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={visible.length === 0 || busy}
                    onChange={toggleAllVisible}
                    title="Tout sélectionner"
                    aria-label="Tout sélectionner"
                  />
                </th>
                <SortableTh field="name" sort={sort} onSort={setSort}>Affaire</SortableTh>
                <SortableTh field="code" sort={sort} onSort={setSort}>Code</SortableTh>
                <SortableTh field="client" sort={sort} onSort={setSort}>Client</SortableTh>
                <SortableTh field="lastActivity" sort={sort} onSort={setSort}>Dernière activité</SortableTh>
                <SortableTh field="lastPlanned" sort={sort} onSort={setSort}>Fin du dernier planning</SortableTh>
                <SortableTh field="status" sort={sort} onSort={setSort}>Statut</SortableTh>
                <th style={th}>Raisons</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 20, color: 'var(--text-muted)' }}>
                    Aucune affaire à archiver selon ces critères.
                  </td>
                </tr>
              ) : (
                visible.map((a) => (
                  <tr
                    key={a.id}
                    style={{
                      borderTop: '1px solid var(--glass-border)',
                      background: selectedIds.has(a.id) ? 'var(--glass)' : undefined,
                    }}
                  >
                    <td style={td}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(a.id)}
                        disabled={busy}
                        onChange={() => toggle(a.id)}
                        aria-label={`Sélectionner ${a.name}`}
                      />
                    </td>
                    <td style={{ ...td, color: 'var(--text)' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                        <span>{a.name}</span>
                        {a.planningOnly ? <PlanningOnlyBadge /> : null}
                        {a.isInternal ? <InternalAffaireBadge size="sm" expiresAt={a.expiresAt} /> : null}
                      </div>
                    </td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>{a.code || '—'}</td>
                    <td style={td}>{a.client?.name ?? '—'}</td>
                    <td style={td}>
                      {a.lastLoggedDate ? fmtDay(a.lastLoggedDate) : (
                        <span title="Jamais pointée : date de création">Créée le {fmtDay(a.createdAt)}</span>
                      )}
                    </td>
                    <td style={td}>{fmtDay(a.lastPlannedDate)}</td>
                    <td style={td}>{STATUS_LABELS[a.status] ?? a.status}</td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>
                      {a.reasons.map((r) => r.label).join(' · ')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
